import { jwtDecode } from "jwt-decode";

class AccessControlManager {
  constructor() {
    this.userPermissions = null;
    this.initializePermissions();
  }

  initializePermissions() {
    try {
      const loginData = localStorage.getItem('usibras_login');
      const userData = localStorage.getItem('usibras_user_data');
      
      if (!loginData && !userData) {
        this.userPermissions = null;
        return;
      }
      
      let decodedToken;
      
      // Try to parse as JSON first (session management format)
      if (loginData) {
        try {
          const parsedData = JSON.parse(loginData);
          if (parsedData.userData) {
            // This is session manager format, decode the userData
            decodedToken = jwtDecode(parsedData.userData);
          } else {
            // Fallback to direct JWT token
            decodedToken = jwtDecode(loginData);
          }
        } catch (parseError) {
          // If JSON parsing fails, try as direct JWT token
          decodedToken = jwtDecode(loginData);
        }
      } else if (userData) {
        // Fallback to direct user data
        decodedToken = jwtDecode(userData);
      }
      
      if (decodedToken) {
        this.userPermissions = {
          role: decodedToken.role,
          unit: decodedToken.unit,
          isAdmin: decodedToken.role === 'admin'
        };
      } else {
        this.userPermissions = null;
      }
    } catch (error) {
      console.error('Error initializing permissions:', error);
      this.userPermissions = null;
    }
  }

  // Define role-based menu access
  getMenuPermissions() {
    if (!this.userPermissions) {
      this.initializePermissions();
      if (!this.userPermissions) return {};
    }

    const { role, unit, isAdmin } = this.userPermissions;

    // Admin has access to everything
    if (isAdmin) {
      return {
        rcnWarehouse: true,
        finishedProducts: true,
        users: true,
        settings: true,
        analystProcess: true,
        cashewPrice: true,
        manualPeeling: true,
        performanceKpi: true,
        rcnDelivery: true
      };
    }

    // Define permissions by unit for regular users
    const unitPermissions = {
      'RCN Warehouse': {
        rcnWarehouse: true,
        finishedProducts: false,
        users: false,
        settings: true,
        analystProcess: false,
        cashewPrice: false,
        manualPeeling: false
      },
      'Finish Pro Warehouse': {
        rcnWarehouse: false,
        finishedProducts: true,
        users: false,
        settings: true,
        analystProcess: false,
        cashewPrice: false,
        manualPeeling: false
      },
      'Price': {
        rcnWarehouse: false,
        finishedProducts: false,
        users: false,
        settings: true,
        analystProcess: false,
        cashewPrice: true,
        manualPeeling: false
      },
      'Analyst': {
        rcnWarehouse: false,
        finishedProducts: false,
        users: false,
        settings: true,
        analystProcess: true,
        cashewPrice: false,
        manualPeeling: false
      },
      'Field Analyst': {
        rcnWarehouse: false,
        finishedProducts: false,
        users: false,
        settings: true,
        analystProcess: true,
        cashewPrice: false,
        manualPeeling: false
      },
      'Manual Peeling': {
        rcnWarehouse: false,
        finishedProducts: false,
        users: false,
        settings: true,
        analystProcess: false,
        cashewPrice: false,
        manualPeeling: true
      },
      'Field Analysis': {
        rcnWarehouse: false,
        finishedProducts: false,
        users: false,
        settings: true,
        analystProcess: true,
        cashewPrice: false,
        manualPeeling: false
      },
      'Factory Analyst': {
        rcnWarehouse: false,
        finishedProducts: false,
        users: false,
        settings: true,
        analystProcess: true,
        cashewPrice: false,
        manualPeeling: false
      },
      'Performance KPI': {
        rcnWarehouse: false,
        finishedProducts: false,
        users: false,
        settings: true,
        analystProcess: false,
        cashewPrice: false,
        manualPeeling: false,
        performanceKpi: true
      },
      'Dispatch RCN': {
        rcnWarehouse: false,
        finishedProducts: false,
        users: false,
        settings: true,
        analystProcess: false,
        cashewPrice: false,
        manualPeeling: false,
        performanceKpi: false,
        rcnDelivery: true
      }
    };

    return unitPermissions[unit] || {
      rcnWarehouse: false,
      finishedProducts: false,
      users: false,
      settings: true,
      analystProcess: false,
      cashewPrice: false,
      manualPeeling: false,
      performanceKpi: false,
      rcnDelivery: false
    };
  }

  canAccessPerformanceKPI() {
    const permissions = this.getMenuPermissions();
    return permissions.performanceKpi === true;
  }

  // Get landing page based on role/unit
  getLandingPage() {
    if (!this.userPermissions) {
      this.initializePermissions();
      if (!this.userPermissions) return '/auth/sign-in';
    }

    const { role, unit } = this.userPermissions;

    if (role === 'admin') return '/admin/warehouse/dashboard';

    const landingPages = {
      'RCN Warehouse': '/admin/warehouse/dashboard',
      'Finish Pro Warehouse': '/admin/finished-products/dashboard',
      'Price': '/admin/cashew-price',
      'Analyst': '/admin/analyst-process/dashboard',
      'Manual Peeling': '/admin/manualpeeling/worker-list',
      'Field Analysis': '/admin/analyst-process/field-analysis',
      'Field Analyst': '/admin/analyst-process/field-analysis',
      'Factory Analyst': '/admin/analyst-process/add-raw-rcn',
      'Performance KPI': '/performance-kpi/dashboard',
      'Dispatch RCN': '/admin/rcn-delivery/dashboard'
    };

    return landingPages[unit] || '/admin/warehouse-add';
  }

  // Check specific menu access
  canAccessMenu(menuName) {
    const permissions = this.getMenuPermissions();
    return permissions[menuName] || false;
  }

  // Get user info
  getUserInfo() {
    if (!this.userPermissions) {
      this.initializePermissions();
    }
    return this.userPermissions;
  }

  // Check if user is admin
  isAdmin() {
    return this.userPermissions?.isAdmin || false;
  }

  // Check if user has specific unit
  hasUnit(unit) {
    return this.userPermissions?.unit === unit;
  }

  // Check if user has specific role
  hasRole(role) {
    return this.userPermissions?.role === role;
  }

  // Refresh permissions (call after login/logout)
  refreshPermissions() {
    this.initializePermissions();
  }

  // Clear permissions (call on logout)
  clearPermissions() {
    this.userPermissions = null;
  }
}

// Export singleton instance
export const accessControl = new AccessControlManager();